import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import BigNumber from 'bignumber.js';
import { forkJoin, from, iif, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';

export interface ResourcePage {
  page: {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
  };
  resources: Resource[];
}


export interface Lan {
  port: string;
  state: string;
  position: string;
  service: string;
  isp: string;
  ontId: string;
  lan: string;
  
}

export interface Resource {
  domain: DomainName;
  companyKey?: string;
  companyName?: string;
  creator?: string;
  lastModifier?: string;
  createdAt: Date;
  modifiedAt?: Date;
  type?: string;
  name: string;
  description?: string;
  lifecycleState: string;
  freePorts: number;
  totalPorts: number;
  specificationName: ResSpecificationId;
  dependsOn?: Resource;
  contains?: Resource[];
  containedBy?: Resource[];
  describedByList?: DescribedBy[];
  lanDetail?: any[];

  locationRef?: {
    name: string;
  }[];

  occupancy?: string;
  state?: string;
  resourceType?: string;
  describedByObject?: { [p: string]: string | null; };
  territoryUser?: string;
  domainDisplay?: string;

  _links: {
    self?: {
      href: string;
    };
    resource?: {
      href: string;
    };
    dependsOn?: {
      href: string;
    };
    contains?: {
      href: string;
    };
    containedBy?: {
      href: string;
    };
    resourceSpec?: {
      href: string;
    };
    locationRef?: {
      href: string;
    };
  };

  [p: string]: any;
}

export enum DomainName {
  DEFAULT = 'DEFAULT',
  ADAMO = 'ADAMO',
  DEMO = 'DEMO',
  CTL = 'CTL',
  OFY = 'OFY',
  TVH = 'TVH',
  MM = 'MM',
  UFINET = 'UFINET',
  VF = 'VF',
  TFY = 'TFY',
  GTL = 'GTL',
  AXT = 'AXT',
  DEV = 'DEV',
  ONIVIA = 'ONIVIA'
}

export type ResSpecificationId =
  'ProvinceSpec'
  | 'TownSpec'
  | 'OLTNodeResSpec'
  | 'OLTResSpec'
  | 'OLTCardResSpec'
  | 'OLTPortResSpec'
  | 'CTOResSpec'
  | 'SplitterResSpec'
  | 'CDPResSpec'
  | 'CTOPortResSpec'
  | 'ONTResSpec'
  | 'C_VLANSpec'
  | 'S_VLANSpec'
  | 'LAN'
  | 'unknown';

export type CharId =
  'name'
  | 'OLT-code'
  | 'OLT-model'
  | 'CTO-code'
  | 'CTO-type'
  | 'Splitter-id'
  | 'Splitter-type'
  | 'ONT-id';

const specMap: {
  [p in ResSpecificationId]: {
    id: string;
    short: string;
    tab?: string;
    multi?: string;
  }
} = {
  ProvinceSpec: { id: 'province', tab: 'provinces', multi: 'Provincias', short: 'Provincia' },
  TownSpec: { id: 'town', tab: 'towns', multi: 'Poblaciones', short: 'Población' },
  OLTNodeResSpec: { id: 'oltnode', short: 'OLTNode' },
  OLTResSpec: { id: 'olt', tab: 'olts', multi: 'OLTs', short: 'OLT' },
  OLTCardResSpec: { id: 'olt-card', tab: 'olt-cards', multi: 'OLT Slots', short: 'OLT Slot' },
  OLTPortResSpec: { id: 'olt-port', tab: 'olt-ports', multi: 'OLT Ports', short: 'OLT Port' },
  CTOResSpec: { id: 'cto', tab: 'ctos', multi: 'CTOs', short: 'CTO' },
  SplitterResSpec: { id: 'splitter', tab: 'splitters', multi: 'Splitters', short: 'Splitter' },
  CDPResSpec: { id: 'cdp', tab: 'cdps', multi: 'CDPs', short: 'CDP' },
  CTOPortResSpec: { id: 'cto-port', tab: 'cto-ports', multi: 'Puertos', short: 'Puerto' },
  ONTResSpec: { id: 'ont', tab: 'onts', multi: 'ONTs', short: 'ONT' },
  C_VLANSpec: { id: 'cvlan', tab: 'cvlans', multi: 'CVLANs', short: 'CVLAN' },
  S_VLANSpec: { id: 'svlan', tab: 'svlans', multi: 'SVLANs', short: 'SVLAN' },
  LAN: { id: 'lan', tab: 'lans', multi: 'LANs', short: 'LAN' },
  unknown: { id: 'unknown', short: 'unknown' }
};

const networkStatesMap = {
  OLTResSpec: {
    FREE: { display: 'Activada', remark: 'normal', help: 'OLT activada' },
    INACTIVE: { display: 'Desactivada', remark: 'warning', help: 'OLT desactivada' },
  },
  CTOResSpec: {
    FREE: { display: 'Activada', remark: 'normal', help: 'CTO activada' },
    INACTIVE: { display: 'Desactivada', remark: 'warning', help: 'CTO desactivada' },
  },
  CTOPortResSpec: {
    FREE: { display: 'Sin conectar', remark: 'disabled', help: 'Puerto no preconectado' },
    PRESELECTED: { display: 'Pre-conectado', remark: 'disabled', help: 'Puerto marcado como preconectado' },
    ACTIVATED: { display: 'Activado', remark: 'normal', help: 'Puerto activado' },
    PREACTIVATED: { display: 'Pre-activado', remark: 'normal', help: 'Este puerto está siendo activado' },
    RESERVED: { display: 'Reservado', remark: 'seminormal', help: 'Puerto reservado' },
  },
  C_VLANSpec: {
    FREE: { display: 'Libre', remark: 'disabled', help: 'CVLAN libre' },
    ACTIVATED: { display: 'Activada', remark: 'normal', help: 'CVLAN activada' },
  }
};

const tuGroupsMap: {
  [p in DomainName]?: {
    domain: string;
    display: string;
    [p: string]: any;
  }
} = {
  UFINET: { domain: 'UFINET', display: 'ONIVIA' },
  ADAMO: { domain: 'ADAMO', display: 'ADAMO' },
  DEMO: { domain: 'DEMO', display: 'DEMO' },
  ONIVIA: { domain: 'ONIVIA', display: 'ONIVIA' },
  MM: {
    domain: 'MM',
    display: 'MásMóvil',
    code: '904',
    svlan: '522',
    templateSuffix: '_MM',
    withPPAI: true,
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'M-28-12599-584445333' }
  },
  TVH: {
    domain: 'TVH',
    display: 'Horadada TV',
    code: '602',
    templateSuffix: '_TVH',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  },
  VF: {
    domain: 'VF',
    display: 'Vodafone',
    code: '082',
    svlan: '101',
    templateSuffix: '_VF',
    withPPAI: true,
    withTVTests: false,
    withVFTests: true,
    testAll: false,
    examples: { remote_ID: 'M-28-12599-584445333' }
  },
  TFY: {
    domain: 'TFY',
    display: 'Telfy',
    code: '803',
    templateSuffix: '_TFY',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  },
  GTL: {
    domain: 'GTL',
    display: 'Gartel',
    code: '401',
    templateSuffix: '_GTL',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  },
  AXT: {
    domain: 'AXT',
    display: 'Axartel',
    code: '433',
    templateSuffix: '_AXT',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  },
  CTL: {
    domain: 'CTL',
    display: 'Citelia',
    code: '876',
    svlan: '624',
    templateSuffix: '_CTL',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  },
  DEV: {
    domain: 'DEV',
    display: 'DevAndHost',
    code: '422',
    templateSuffix: '_DEV',
    withTVTests: true,
    testAll: true,
    examples: { remote_ID: 'ZTEEQBXGB706816' }
  }
};

interface DescribedBy {
  value: string | null;
  _links: {
    resourceCharRef: {
      href: string;
    };
  };
}

export interface OltCardPortStats {
  cardNumber: number;
  freePortsNumber: number;
  portCapacity: number;
  portNumber: number;
  totalPortsNumber: number;
  occupiedPortsNumber: number;
  occupiedPortsRatio: string;
  occupiedPortsRatioNumber: number;
  freePortsRatio: string;
  capacityRatio: string;
}

export function describedByListToObject(describedByList: DescribedBy[]): { [p: string]: string | null; } {
  const response: { [p: string]: string | null; } = {};

  for (const describedBy of describedByList) {
    const href = describedBy._links.resourceCharRef.href;
    const hrefParts = href.split('/');

    response[hrefParts[hrefParts.length - 1]] = describedBy.value;
  }

  return response;
}

export function resourceListToResourceTypeMap(resources: Resource[]): { [p in ResSpecificationId]?: Resource[]; } {
  const resourceTypeMap: { [p in ResSpecificationId]?: Resource[]; } = {};

  if (resources) {
    for (const resource of resources) {
      const resourceType = resource.specificationName;

      if (resourceType) {
        if (!resourceTypeMap.hasOwnProperty(resourceType)) {
          resourceTypeMap[resourceType] = [];
        }

        resourceTypeMap[resourceType].push(resource);
      }
    }
  }

  return resourceTypeMap;
}

@Injectable()
export class FtthResourceService {

  appData: any;

  constructor(
    private httpClient: HttpClient,
    appService: AppService,
  ) {
    this.appData = appService.settings;
  }

  private static getPageAndSort(page: number, size: number, sortBy: string, sortDirection: 'asc' | 'desc' | '') {
    let params = new HttpParams()
      .append('page', page.toString())
      .append('size', size.toString());

    if (sortBy && sortDirection) {
      params = params.append('sort', `${sortBy},${sortDirection}`);
    }

    return params;
  }

  private static getOccupancy(freePorts: number, totalPorts: number): string {
    if (!totalPorts) {
      return '0.00';
    }

    const freePortsBN = new BigNumber(freePorts);
    const totalPortsBN = new BigNumber(totalPorts);

    const occupiedPortsBN = totalPortsBN.minus(freePortsBN);

    return occupiedPortsBN.dividedBy(totalPortsBN)
      .multipliedBy(new BigNumber(100))
      .toFormat(2);
  }

  private static getState(specificationName: string, lifecycleState: string): string | null {
    // tslint:disable-next-line:max-line-length
    if (specificationName && lifecycleState && specificationName in networkStatesMap && lifecycleState in networkStatesMap[specificationName]) {
      return networkStatesMap[specificationName][lifecycleState].display;
    }
    return null;
  }

  static getResourceType(specificationName: ResSpecificationId): string {
    if (specificationName && specificationName in specMap) {
      return specMap[specificationName].short;
    }

    return specificationName;
  }

  private static getTerritoryUser(tuCode?: string): string | null {
    if (!tuCode) {
      return;
    }

    for (const tuKey of Object.keys(tuGroupsMap)) {
      if (tuGroupsMap.hasOwnProperty(tuKey)) {
        const group = tuGroupsMap[tuKey];
        if (group.code && group.code === tuCode) {
          return `${group.display} (${tuCode})`;
        }
      }
    }

    return tuCode;
  }

  getResources(resSpecificationId: ResSpecificationId | '',
    charId: CharId | '',
    charValue: string,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | '',
    ignoreStats: boolean = false): Observable<ResourcePage> {
    if (resSpecificationId === '') {
      if (!charValue) {
        return this.getAllResources(page, size, sortBy, sortDirection);
      } else {
        return this.findByNameLike(charValue, page, size, sortBy, sortDirection);
      }
    }


    switch (charId) {
      case '':
        return this.findResourcesBySpecificationPaged(resSpecificationId, page, size, sortBy, sortDirection, ignoreStats);
      case 'name':
        return this.findResourcesBySpecificationAndNamePaged(charValue, resSpecificationId, page, size, sortBy, sortDirection);
      default:
        // tslint:disable-next-line:max-line-length
        return this.findResourceByCharacteristicValueAndSpecification(charId, charValue, resSpecificationId, page, size, sortBy, sortDirection);
    }
  }

  getCVLANPaged(namingId: string,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);
    params = params.append('name', namingId)
      .append('resSpecificationId', 'C_VLANSpec');

    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findResourcesBySpecificationAndNamePaged`, { params })
      .pipe(
        map(resourcePage => {
          const resources = resourcePage._embedded.resources.map(resourceResponse => {
            const describedByObject = describedByListToObject(resourceResponse.describedByList);

            const resource: Resource = Object.assign({}, resourceResponse);
            Object.assign(resource, describedByObject);
            resource.describedByObject = describedByObject;

            if (resource.specificationName === 'C_VLANSpec') {
              const name = resource.name;
              resource.territoryUser = FtthResourceService.getTerritoryUser(name.substring(name.length - 3, name.length));
            }
            return resource;
          });

          return {
            page: resourcePage.page,
            resources,
          };
        })
      );

  }

  getOltCardPortStats(namingId: string): Observable<OltCardPortStats[][]> {

    return this.httpClient.get<{
      cardNumber: string;
      freePortsNumber: number;
      portCapacity: string;
      portNumber: string;
      totalPortsNumber: number;
    }[]>(`${this.appData.webApi}/resources-inventory/ftth/management/olt/${namingId}/oltCardPortStats`)
      .pipe(
        map(rawStats => {
          const cards = Math.max(...rawStats.map(rawStat => +rawStat.cardNumber)) + 1;
          const ports = Math.max(...rawStats.map(rawStat => +rawStat.portNumber)) + 1;

          const enhancedStats: OltCardPortStats[][] = [];
          for (let c = 0; c < cards; c++) {
            const card = [];
            for (let p = 0; p < ports; p++) {
              card.push(null);
            }
            enhancedStats.push(card);
          }

          for (const rawStat of rawStats) {
            const stat: OltCardPortStats = {
              cardNumber: +rawStat.cardNumber,
              freePortsNumber: rawStat.freePortsNumber,
              portCapacity: +rawStat.portCapacity,
              portNumber: +rawStat.portNumber,
              totalPortsNumber: rawStat.totalPortsNumber,
              occupiedPortsNumber: (rawStat.totalPortsNumber - rawStat.freePortsNumber),
              // tslint:disable-next-line:max-line-length
              occupiedPortsRatio: `${(((rawStat.totalPortsNumber - rawStat.freePortsNumber) / rawStat.totalPortsNumber) * 100).toFixed(1)}%`,
              occupiedPortsRatioNumber: (rawStat.totalPortsNumber - rawStat.freePortsNumber) / rawStat.totalPortsNumber,
              freePortsRatio: `${((rawStat.freePortsNumber / rawStat.totalPortsNumber) * 100).toFixed(1)}%`,
              capacityRatio: `${(((rawStat.totalPortsNumber - rawStat.freePortsNumber) / +rawStat.portCapacity) * 100).toFixed(1)}%`,
            };

            enhancedStats[stat.cardNumber][stat.portNumber] = stat;
          }

          return enhancedStats;
        }),
      );

  }

  private initResource(resourceResponse: Resource, direction: 'up' | 'down' | 'both' | 'none' = 'both', recursive: boolean = true): Observable<Resource> {
    return this.getStats(Object.assign({}, resourceResponse))
      .pipe(
        map(resource => {
          const describedByObject = describedByListToObject(resourceResponse.describedByList);
          Object.assign(resource, describedByObject);
          resource.describedByObject = describedByObject;
          resource.domainDisplay = tuGroupsMap[resource.domain].display;

          const hrefLinkChunks = resource._links.self.href.split('/');
          const type = hrefLinkChunks[hrefLinkChunks.length - 1];
          const namingId = hrefLinkChunks[hrefLinkChunks.length - 1];

          if (resource.specificationName === 'CTOPortResSpec' && resource.describedByObject.hasOwnProperty('CTOPort-territoryUser')) {
            resource.territoryUser = FtthResourceService.getTerritoryUser(resource.describedByObject['CTOPort-territoryUser']);
          }

          if (resource.specificationName === 'C_VLANSpec') {
            const name = resource.name;
            resource.territoryUser = FtthResourceService.getTerritoryUser(name.substring(name.length - 3, name.length));
          }
          return resource;
        }),
        switchMap(resource => forkJoin(
          of(resource),
          iif(
            () => !!((direction === 'both' || direction === 'down') && resource._links.contains && recursive),
            this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/${resource._links.contains.href.split('/')[resource._links.contains.href.split('/').length - 3]}/${resource._links.contains.href.split('/')[resource._links.contains.href.split('/').length - 2]}/contains`)
              .pipe(
                catchError(() => {
                  return of();
                }),
                map((response) => {
                  if (response) {
                    return this.joinResources(response._embedded);
                  }
                }),
              )
            ,
            of([]))
            .pipe(
              switchMap(resources => from(resources)),
              mergeMap((containedResource: Resource) => this.initResource(containedResource, 'down', false)),
              toArray(),
            ),
          iif(
            () => !!((direction === 'both' || direction === 'up') && resource._links.containedBy && recursive),
            this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/${resource._links.containedBy.href.split('/')[resource._links.containedBy.href.split('/').length - 3]}/${resource._links.containedBy.href.split('/')[resource._links.containedBy.href.split('/').length - 2]}/containedBy`)
              .pipe(
                catchError(() => {
                  return of();
                }),
                map((response) => {
                  if (response) {
                    return this.joinResources(response._embedded);
                  }
                }),
              )
            ,
            of([]))
            .pipe(
              switchMap(resources => from(resources)),
              mergeMap((containedResource: Resource) => this.initResource(containedResource, 'up', false)),
              toArray(),
            ),
        )),
        map(([resource, containsResources, containedByResources]) => {
          resource.contains = containsResources;
          resource.containedBy = containedByResources;
          return resource;
        })
      );
  }

  getResource(namingId: string): Observable<Resource> {
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/${namingId}`)
      .pipe(
        switchMap(resourceResponse => this.initResource(resourceResponse)),
      );
  }

  getLan(namingId: string): any {  
      const result= this.httpClient.get<any>(`${this.appData.webApi}/service-test/timbrado_test/${namingId}/containedBy`) 
      .pipe(
        map(response => response),
      );
      return result;
  }

  enable(namingId: string): Observable<void> {
    return this.httpClient.put<void>(`${this.appData.webApi}/resources-inventory/ftth/management/resources/${namingId}/enable`, null);
  }

  disable(namingId: string): Observable<void> {
    return this.httpClient.put<void>(`${this.appData.webApi}/resources-inventory/ftth/management/resources/${namingId}/disable`, null);
  }

  generateExportFile(namingId: string): Observable<string> {
    const params = new HttpParams().append('resourceNamingId', namingId);
    return this.httpClient.post<{ reportFilename: string }>(`${this.appData.webApi}/resources-inventory/ftth/management/report`, null, { params })
      .pipe(
        map(response => response.reportFilename),
      );
  }

  exportFileIsOpen(fileName: string): Observable<string> {
    const params = new HttpParams().append('fileName', fileName);
    return this.httpClient.get<{ state: string; }>(`${this.appData.webApi}/resources-inventory/ftth/management/report/isOpen`, { params })
      .pipe(
        map(response => response.state),
      );
  }

  downloadFile(fileName: string): Observable<ArrayBuffer> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });
    return this.httpClient.get(`${this.appData.webApi}/resources-inventory/reports/${fileName}`, { headers, responseType: 'arraybuffer' });
  }

  preselect(namingId: string): Observable<void> {
    return this.httpClient.put<void>(`${this.appData.webApi}/resources-inventory/ftth/management/ctoPorts/${namingId}/preselect`, null);
  }

  deselect(namingId: string): Observable<void> {
    return this.httpClient.put<void>(`${this.appData.webApi}/resources-inventory/ftth/management/ctoPorts/${namingId}/deselect`, null);
  }

  moveCTO(ctoNamingId: string, oltPortNamingId: string): Observable<void> {
    const body = {
      oltPortNamingId
    };
    return this.httpClient.put<void>(`${this.appData.webApi}/resources-inventory/ftth/management/cto/${ctoNamingId}/changeoltport`, body);
  }

  getAffectedCustomers(oltCode: string, slot: string, oltPort: string): Observable<any> {
    const params = new HttpParams().append("oltCode", oltCode).append("slot", slot).append("oltPort", oltPort);
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/ftth/management/customers`, { params });
  }

  getParents(resource: Resource): Observable<any> {
    let params = new HttpParams().append('name', resource.namingId);
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/findParents`, { params });
  }

  private findResourceByCharacteristicValueAndSpecification(charId: CharId,
    charValue: string,
    resSpecificationId: ResSpecificationId,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    params = params.append('resSpecificationId', resSpecificationId)
      .append('resourceCharNamingId', charId)
      .append('charValue', charValue);

    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findResourceByCharacteristicValueAndSpecification`, { params })
      .pipe(
        this.toPage(),
      );
  }

  private findResourcesBySpecificationAndNamePaged(name: string,
    resSpecificationId: ResSpecificationId,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    params = params.append('resSpecificationId', resSpecificationId)
      .append('name', `%${name}%`);

    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findResourcesBySpecificationAndNamePaged`, { params })
      .pipe(
        this.toPage(),
      );
  }

  private findResourcesBySpecificationPaged(resSpecificationId: ResSpecificationId,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | '',
    ignoreStats: boolean = false): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    params = params.append('resSpecificationId', resSpecificationId);

    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findResourcesBySpecificationPaged`, { params })
      .pipe(
        (!ignoreStats ? this.toPage() : this.toCombo()),
      );
  }

  private findByNameLike(name: string,
    page: number,
    size: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    params = params.append('name', `%${name}%`);

    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findByNameLike`, { params })
      .pipe(
        this.toPage(),
      );
  }

  private getAllResources(page: number, size: number, sortBy?: string, sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    const params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources`, { params })
      .pipe(
        this.toPage(),
      );
  }

  private toPage(): OperatorFunction<any, ResourcePage> {
    const _this = this;
    return input$ => input$.pipe(
      switchMap(resourcesResponse => forkJoin([of(resourcesResponse.page), _this.initResources(_this.joinResources(resourcesResponse._embedded))])),
      map(([pageResponse, resources]) => ({ page: pageResponse, resources })),
    );
  }

  private toCombo(): OperatorFunction<any, ResourcePage> {
    const _this = this;
    return input$ => input$.pipe(
      switchMap(resourcesResponse => forkJoin(of(resourcesResponse.page), of(_this.joinResources(resourcesResponse._embedded)))),
      map(([pageResponse, resources]) => ({ page: pageResponse, resources })),
    );
  }

  private joinResources(resources: any): Resource[] {
    let allResources = [];
    if (resources.logicalDevices) {
      allResources = [...allResources, ...resources.logicalDevices]
    }
    if (resources.physicalDevices) {
      allResources = [...allResources, ...resources.physicalDevices]
    }
    if (resources.deviceInterfaces) {
      allResources = [...allResources, ...resources.deviceInterfaces]
    }
    if (resources.interfacePorts) {
      allResources = [...allResources, ...resources.interfacePorts]
    }
    return allResources;
  }

  private initResources(resourcesResponse: Partial<Resource>[]): Observable<Resource[]> {
    return iif(() => resourcesResponse.length > 0,
      forkJoin(resourcesResponse.map(resource => {
        const fullResource: Resource = Object.assign({} as Resource, resource);
        return this.getStats(fullResource);
      })),
      of([])
    );
  }

  private getStats(resource: Resource): Observable<Resource> {
    const hrefLinkChunks = resource._links.self.href.split('/');

    //const params = new HttpParams({encoder: new HttpUrlEncodingCodec()}).append('resourceNamingId', decodeURIComponent(hrefLinkChunks[hrefLinkChunks.length - 1]));

    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/ftth/management/resources/${decodeURIComponent(hrefLinkChunks[hrefLinkChunks.length - 1])}/stats`)
      .pipe(
        catchError(() => {
          return of(resource);
        }),
        map(stat => {
          if (stat) {
            resource.freePorts = stat.freePorts;
            resource.totalPorts = stat.totalPorts;
            resource.specificationName = stat.specificationName;
            resource.occupancy = FtthResourceService.getOccupancy(stat.freePorts, stat.totalPorts);
            resource.state = FtthResourceService.getState(stat.specificationName, resource.lifecycleState);
            resource.resourceType = FtthResourceService.getResourceType(stat.specificationName);
            return resource;
          }
        })
      );
  }


  getResourceBySpecNameLifecycleOrdered(name: string, resSpecificationId: ResSpecificationId | '',
  page: number,
  size: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc' | ''): Observable<ResourcePage> {
    let params = FtthResourceService.getPageAndSort(page, size, sortBy, sortDirection);

    params = params.append('resSpecificationId', resSpecificationId);
    params = params.append('lifecycleState', "FREE");
    params = params.append('name', name);

    return this.httpClient.get<any>(`${this.appData.webApi}/resources-inventory/resources/search/findByResourceSpecNamingIdAndLifecycleStateOrdered`, { params })
      .pipe(
        (this.toCombo()),
      );
}


}
